import React, { useEffect, useRef } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { DemandInsight } from './types';
import { TRIP_RENDER_CAP } from '../map/components/TripLayerRenderer';

import { opsColors } from '../../theme/opsTokens';

interface DemandEstimationStepProps {
  demandTrips: number | null;
  serviceStartTimeUtc: string;
  serviceEndTimeUtc: string;
  demandHelpStatus: 'idle' | 'loading' | 'ready' | 'error';
  demandError: string | null;
  demandInsight: DemandInsight | null;
  helperStatusText?: string | null;
  onDemandTripsChange: (value: number | null) => void;
  onServiceStartTimeChange: (value: string) => void;
  onServiceEndTimeChange: (value: string) => void;
  onHelpMe: () => Promise<void>;
  onGenerateTrips: () => Promise<void>;
  onEditPolygonOnMap?: () => void;
  showEditPolygonOnMapAction?: boolean;
  maxGeneratedTrips?: number;
  focusDemandInputSignal?: number;
  emphasizeLimitWarning?: boolean;
}

export default function DemandEstimationStep({
  demandTrips,
  serviceStartTimeUtc,
  serviceEndTimeUtc,
  demandHelpStatus,
  demandError,
  demandInsight,
  helperStatusText,
  onDemandTripsChange,
  onServiceStartTimeChange,
  onServiceEndTimeChange,
  onHelpMe,
  onGenerateTrips,
  onEditPolygonOnMap,
  showEditPolygonOnMapAction = false,
  maxGeneratedTrips = 250,
  focusDemandInputSignal = 0,
  emphasizeLimitWarning = false,
}: DemandEstimationStepProps) {
  const { t } = useTranslation('onboarding');
  const exceedsRenderPreviewCap = typeof demandTrips === 'number' && demandTrips > TRIP_RENDER_CAP;
  const exceedsGeneratedTripLimit = typeof demandTrips === 'number' && demandTrips > maxGeneratedTrips;
  const demandInputRef = useRef<HTMLInputElement | null>(null);
  const showOrangeEmphasis = exceedsGeneratedTripLimit && emphasizeLimitWarning;

  useEffect(() => {
    if (!focusDemandInputSignal || !exceedsGeneratedTripLimit) {
      return;
    }
    demandInputRef.current?.focus();
    demandInputRef.current?.select();
  }, [exceedsGeneratedTripLimit, focusDemandInputSignal]);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1.5 }}>
        {t('demand.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('demand.description')}
      </Typography>

      <Box
        sx={(theme) => ({
          border: `1px solid ${opsColors.neutral.line}`,
          borderRadius: 2,
          backgroundColor: opsColors.neutral.panel,
          p: 2,
          mb: 2,
        })}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
          <TextField
            label={t('demand.serviceStartTime')}
              type="time"
              value={serviceStartTimeUtc}
              onChange={(event) => onServiceStartTimeChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
            />
            <TextField
              label={t('demand.serviceEndTime')}
              type="time"
              value={serviceEndTimeUtc}
              onChange={(event) => onServiceEndTimeChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
            />
            <TextField
              label={t('demand.expectedTripsPerDay')}
              type="number"
              inputRef={demandInputRef}
              value={demandTrips ?? ''}
              onChange={(event) => {
                const value = event.target.value.trim();
                onDemandTripsChange(value ? Math.max(0, Number(value)) : null);
              }}
              error={exceedsGeneratedTripLimit}
              helperText={
                exceedsGeneratedTripLimit
                  ? t('demand.tripLimitHelper', { max: maxGeneratedTrips })
                  : undefined
              }
              sx={(theme) => ({
                width: { xs: '100%', sm: 220 },
                minWidth: { sm: 220 },
                ...(showOrangeEmphasis
                  ? {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: opsColors.semantic.warn.soft,
                        '& fieldset': {
                          borderColor: opsColors.semantic.warn.color,
                          borderWidth: 2,
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.warning.dark,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.warning.dark,
                        },
                      },
                    }
                  : {}),
              })}
            />
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Button
                variant="contained"
                onClick={() => void onGenerateTrips()}
                disabled={
                  demandHelpStatus === 'loading' ||
                  typeof demandTrips !== 'number' ||
                  demandTrips <= 0 ||
                  exceedsGeneratedTripLimit
                }
                sx={{ alignSelf: 'center' }}
              >
                {t('demand.generateTrips')}
              </Button>
            </Box>
          </Box>
        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'grid', gap: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('demand.needHelpHeading')}
            </Typography>
            <Typography color="text.secondary">
              {t('demand.needHelpDescription')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={onHelpMe}
              disabled={demandHelpStatus === 'loading'}
              data-testid="wizard.demand.estimate-for-me"
            >
              {demandHelpStatus === 'loading' ? <CircularProgress size={18} color="inherit" /> : t('demand.estimateForMe')}
            </Button>
          </Box>
        </Box>

        {demandError ? (
          <Alert
            severity="warning"
            action={
              showEditPolygonOnMapAction && onEditPolygonOnMap ? (
                <Button color="inherit" size="small" onClick={onEditPolygonOnMap}>
                  {t('demand.editPolygonOnMap')}
                </Button>
              ) : undefined
            }
          >
            {demandError}
          </Alert>
        ) : null}
        {exceedsGeneratedTripLimit ? (
          <Alert severity="error" sx={{ mt: 1.25 }}>
            {t('demand.tripLimitExceeded', { max: maxGeneratedTrips.toLocaleString() })}
          </Alert>
        ) : null}
        {demandHelpStatus === 'ready' && demandInsight ? (
          <Alert severity="info">
            {t('demand.populationApplied')}
          </Alert>
        ) : null}
        {helperStatusText ? (
          <Alert severity="success" sx={{ mt: 1.25 }}>
            {helperStatusText}
          </Alert>
        ) : null}
        {exceedsRenderPreviewCap ? (
          <Alert severity="info" sx={{ mt: 1.25 }}>
            {t('demand.renderPreviewCap', { cap: TRIP_RENDER_CAP.toLocaleString(), total: demandTrips?.toLocaleString() })}
          </Alert>
        ) : null}
      </Box>

      {demandInsight ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
          <Box
            sx={(theme) => ({
              p: 1.75,
              borderRadius: 2,
              border: `1px solid ${opsColors.neutral.line}`,
              backgroundColor: opsColors.neutral.panel,
            })}
          >
            <Typography variant="caption" color="text.secondary">
              {t('demand.totalPopulation')}
            </Typography>
            <Typography variant="h6">{demandInsight.totalPopulation.toLocaleString()}</Typography>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
